/**
 * Simple A5 print slips for Service Advances:
 *   - Advance Receipt   (money-in)  — printed at collection time
 *   - Refund Voucher    (money-out) — printed on refund
 *
 * We reuse the pattern from ReceiptPrintMenu.jsx (window.open + write HTML)
 * so the browser handles pagination / preview / printing natively.
 */
import { api } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";

const escHtml = (s) =>
  String(s ?? "").replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");

function numToIndianWords(n) {
  n = Math.round(Number(n) || 0);
  if (n === 0) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const twoDigits = (x) => (x < 10 ? ones[x] : x < 20 ? teens[x - 10] : tens[Math.floor(x / 10)] + (x % 10 ? " " + ones[x % 10] : ""));
  const threeDigits = (x) => {
    const h = Math.floor(x / 100);
    const rest = x % 100;
    let s = "";
    if (h) s += ones[h] + " Hundred";
    if (rest) s += (h ? " " : "") + twoDigits(rest);
    return s;
  };
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  const hundred = n;
  const parts = [];
  if (crore) parts.push(threeDigits(crore) + " Crore");
  if (lakh) parts.push(twoDigits(lakh) + " Lakh");
  if (thousand) parts.push(twoDigits(thousand) + " Thousand");
  if (hundred) parts.push(threeDigits(hundred));
  return parts.join(" ").replace(/\s+/g, " ").trim() + " Only";
}

const TYPE_LABEL = {
  battery_warranty: "Battery Warranty",
  accident_engine: "Accident / Engine",
  direct_parts: "Direct Parts",
};

const styles = `
<style>
  *{box-sizing:border-box}
  body{font-family:'Times New Roman',serif;color:#111;margin:16px;background:#f6f6f9}
  .slip{max-width:560px;margin:0 auto;background:#fff;border:1px solid #ddd;padding:22px 24px;font-size:12px;line-height:1.45;position:relative}
  .toolbar{max-width:560px;margin:0 auto 10px;display:flex;justify-content:space-between;align-items:center}
  .toolbar .left{font-family:'IBM Plex Mono',monospace;font-size:10px;text-transform:uppercase;letter-spacing:.2em;color:#555}
  .toolbar button{background:#1F60FF;color:#fff;border:0;padding:8px 14px;font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.1em;text-transform:uppercase;cursor:pointer}
  .toolbar button:hover{background:#0A0A0A}
  .hdr{display:flex;align-items:flex-start;gap:16px;margin-bottom:6px}
  .logo{max-height:60px;max-width:140px;object-fit:contain}
  .logo-fallback{font-family:'Cabinet Grotesk',sans-serif;font-weight:800;font-size:16px;color:#111}
  .co{font-size:11px;color:#333}
  .title{text-align:center;font-family:'Cabinet Grotesk',sans-serif;font-size:16px;font-weight:800;text-transform:uppercase;letter-spacing:.14em;margin:10px 0 4px;border-top:1px solid #000;border-bottom:1px solid #000;padding:6px 0}
  .refund .title{color:#a11}
  .meta{display:flex;justify-content:space-between;font-size:11px;margin:6px 0 8px;font-family:'IBM Plex Mono',monospace}
  .kv{display:flex;padding:3px 0;font-size:12px}
  .kv .k{width:130px;color:#555}
  .kv .v{flex:1;font-weight:600}
  .mono{font-family:'IBM Plex Mono',monospace}
  .amt-row{margin:10px 0;padding:10px 12px;background:#f5f5f5;border:1px solid #ddd;display:flex;justify-content:space-between;align-items:center}
  .amt-row .lbl{font-family:'IBM Plex Mono',monospace;font-size:11px;text-transform:uppercase;letter-spacing:.1em;color:#333}
  .amt-row .amt{font-family:'Cabinet Grotesk',sans-serif;font-size:22px;font-weight:800}
  .words{font-size:11.5px;font-style:italic;margin:4px 0 12px}
  .section-title{font-family:'IBM Plex Mono',monospace;font-size:9px;letter-spacing:.2em;text-transform:uppercase;color:#666;margin:12px 0 4px;border-bottom:1px dashed #bbb;padding-bottom:2px}
  .notes{font-size:11.5px;padding:6px 0;border-top:1px dashed #bbb;color:#333;min-height:22px}
  .sig{display:flex;justify-content:space-between;margin-top:34px;gap:20px}
  .sig-col{flex:1}
  .sig-line{border-bottom:1px solid #000;height:28px;margin-bottom:3px}
  .sig-label{font-size:10.5px;color:#333}
  .foot{margin-top:16px;font-size:10px;color:#666;text-align:center;font-family:'IBM Plex Mono',monospace;letter-spacing:.08em}
  @media print{
    body{background:#fff;margin:0}
    .toolbar,.no-print{display:none !important}
    .slip{border:none;padding:12px 16px}
    @page{size:A5;margin:8mm}
  }
</style>
`;

function headerHtml(s) {
  return `<header class="hdr">
    <div>
      ${s.logo_url ? `<img class="logo" src="${escHtml(s.logo_url)}" alt="logo"/>` : `<div class="logo-fallback">${escHtml(s.name || "DEALERSHIP").slice(0, 18)}</div>`}
    </div>
    <div class="co">
      <div style="font-weight:700;font-size:12.5px">${escHtml(s.name || "—")}</div>
      <div>${escHtml((s.address || "").split("\n").join(", "))}</div>
      <div>GSTIN: <b>${escHtml(s.gstin || "—")}</b></div>
    </div>
  </header>`;
}

function batteryBlock(a) {
  if (a.advance_type !== "battery_warranty") return "";
  const rows = [
    ["Old Battery #", a.old_battery_no],
    ["Temp / Stand-by Battery #", a.temp_battery_no],
  ];
  if (a.warranty_battery_no) rows.push(["Warranty Battery #", a.warranty_battery_no]);
  if (a.warranty_received_at) rows.push(["Warranty Received", fmtDate(a.warranty_received_at)]);
  return `<div class="section-title">Battery Tracking</div>` +
    rows.map(([k, v]) => `<div class="kv"><div class="k">${escHtml(k)}</div><div class="v mono">${escHtml(v || "—")}</div></div>`).join("");
}

function advanceReceiptHtml(a, s) {
  const total = Number(a.amount || 0);
  return `<!doctype html><html><head><title>Advance Receipt · ${escHtml(a.advance_no)}</title>${styles}</head>
<body>
<div class="toolbar no-print">
  <div class="left">Service Advance Receipt · ${escHtml(a.advance_no)}</div>
  <button onclick="window.print()">Print</button>
</div>
<div class="slip">
  ${headerHtml(s)}
  <div class="title">Advance Receipt</div>
  <div class="meta">
    <div>Advance No: <b>${escHtml(a.advance_no)}</b></div>
    <div>Receipt No: <b>${escHtml(a.receipt_no || "—")}</b></div>
    <div>Date: <b>${escHtml(fmtDate(a.date))}</b></div>
  </div>
  <div class="section-title">Customer</div>
  <div class="kv"><div class="k">Name</div><div class="v">${escHtml(a.customer_name || "—")}</div></div>
  <div class="kv"><div class="k">Mobile</div><div class="v mono">${escHtml(a.customer_phone || "—")}</div></div>
  <div class="kv"><div class="k">Vehicle Reg #</div><div class="v mono">${escHtml(a.vehicle_reg_no || "—")}</div></div>
  <div class="kv"><div class="k">Job Card #</div><div class="v mono">${escHtml(a.jc_no || "—")}</div></div>
  <div class="kv"><div class="k">Advance Type</div><div class="v">${escHtml(TYPE_LABEL[a.advance_type] || a.advance_type || "—")}</div></div>

  ${batteryBlock(a)}

  <div class="amt-row">
    <div class="lbl">Amount Received (${escHtml((a.payment_method || "").toUpperCase())})</div>
    <div class="amt">₹ ${escHtml(fmtMoney(total))}</div>
  </div>
  <div class="words">In words: <b>${escHtml(numToIndianWords(total))} Rupees</b></div>
  ${a.upi_ref ? `<div class="kv"><div class="k">UPI / UTR Ref</div><div class="v mono">${escHtml(a.upi_ref)}</div></div>` : ""}

  <div class="section-title">Reason / Details</div>
  <div class="notes">${escHtml(a.reason || "—")}</div>

  <div class="sig">
    <div class="sig-col"><div class="sig-line"></div><div class="sig-label">Customer Signature</div></div>
    <div class="sig-col"><div class="sig-line"></div><div class="sig-label">For ${escHtml(s.name || "Dealership")}<br/>Authorised Signatory</div></div>
  </div>
  <div class="foot">This receipt acknowledges an advance held against a future service invoice / job card.</div>
</div>
<script>window.onload=function(){setTimeout(function(){window.focus();},200);};</script>
</body></html>`;
}

function refundVoucherHtml(a, s) {
  const amt = Number(a.refund_amount || 0);
  return `<!doctype html><html><head><title>Refund Voucher · ${escHtml(a.refund_voucher_no || a.advance_no)}</title>${styles}</head>
<body>
<div class="toolbar no-print">
  <div class="left">Refund Voucher · ${escHtml(a.refund_voucher_no || "—")}</div>
  <button onclick="window.print()">Print</button>
</div>
<div class="slip refund">
  ${headerHtml(s)}
  <div class="title">Advance Refund Voucher</div>
  <div class="meta">
    <div>Voucher No: <b>${escHtml(a.refund_voucher_no || "—")}</b></div>
    <div>Against Advance: <b>${escHtml(a.advance_no || "—")}</b></div>
    <div>Refund Date: <b>${escHtml(fmtDate((a.refunded_at || "").slice(0, 10)))}</b></div>
  </div>

  <div class="section-title">Refunded To</div>
  <div class="kv"><div class="k">Name</div><div class="v">${escHtml(a.customer_name || "—")}</div></div>
  <div class="kv"><div class="k">Mobile</div><div class="v mono">${escHtml(a.customer_phone || "—")}</div></div>
  <div class="kv"><div class="k">Vehicle Reg #</div><div class="v mono">${escHtml(a.vehicle_reg_no || "—")}</div></div>
  <div class="kv"><div class="k">Job Card #</div><div class="v mono">${escHtml(a.jc_no || "—")}</div></div>

  ${batteryBlock(a)}

  <div class="section-title">Original Advance</div>
  <div class="kv"><div class="k">Advance Amount</div><div class="v mono">₹ ${escHtml(fmtMoney(a.amount))}</div></div>
  <div class="kv"><div class="k">Adjusted</div><div class="v mono">₹ ${escHtml(fmtMoney(a.adjusted_amount || 0))}</div></div>
  <div class="kv"><div class="k">Original Advance Date</div><div class="v">${escHtml(fmtDate(a.date))}</div></div>

  <div class="amt-row">
    <div class="lbl">Amount Refunded (${escHtml((a.refund_method || "").toUpperCase())})</div>
    <div class="amt">₹ ${escHtml(fmtMoney(amt))}</div>
  </div>
  <div class="words">In words: <b>${escHtml(numToIndianWords(amt))} Rupees</b></div>
  ${a.refund_ref ? `<div class="kv"><div class="k">UPI / UTR Ref</div><div class="v mono">${escHtml(a.refund_ref)}</div></div>` : ""}

  <div class="section-title">Refund Note</div>
  <div class="notes">${escHtml(a.refund_note || a.reason || "—")}</div>

  <div class="sig">
    <div class="sig-col"><div class="sig-line"></div><div class="sig-label">Customer Signature<br/>(Received the refund)</div></div>
    <div class="sig-col"><div class="sig-line"></div><div class="sig-label">For ${escHtml(s.name || "Dealership")}<br/>Authorised Signatory</div></div>
  </div>
  <div class="foot">Payment voucher auto-posted to Cashbook — ref: ${escHtml(a.refund_voucher_no || "—")}</div>
</div>
<script>window.onload=function(){setTimeout(function(){window.focus();},200);};</script>
</body></html>`;
}

let _settingsCache = null;
async function loadSettings() {
  if (_settingsCache) return _settingsCache;
  try {
    const { data } = await api.get("/settings/dealership");
    _settingsCache = data || {};
  } catch {
    _settingsCache = {};
  }
  return _settingsCache;
}

export async function printAdvanceReceipt(advance) {
  const settings = await loadSettings();
  const w = window.open("", "_blank", "width=720,height=920");
  if (!w) return;
  w.document.write(advanceReceiptHtml(advance, settings));
  w.document.close();
}

export async function printRefundVoucher(advance) {
  const settings = await loadSettings();
  const w = window.open("", "_blank", "width=720,height=920");
  if (!w) return;
  w.document.write(refundVoucherHtml(advance, settings));
  w.document.close();
}
